// disable tslint for this file
// tslint:disable
import React, { useState, useCallback, useMemo } from 'react';
import classnames from 'classnames';
import { ActionButton, Link } from '@fluentui/react';
import { createUseStyles } from 'react-jss';

import { ShortHeadline, LongHeadline, Description } from './text-container';

import {
  LikeThumb, DislikeThumb,
} from './assets';
import { useLogger } from '../Logger';

export type AdPlacementCardPreviewProps = {
  shortHeadline?: string,
  longHeadline?: string,
  description?: string,
  finalUrl: string,
  imageSrc?: string,
  lowPrice?: number,
  price?: number,
  priceCurrencySymbol?: string,
  shippingDescription?: string,
};

const styleOverrides = {
  adPlacementContainer: {
    display: 'flex',
    flexDirection: 'column',
    '& .adPlacementCardPreview': {
      marginRight: 16,
      width: 122,
      height: 300,
      '& .clickableCard': {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: 265,
        background: 'radial-gradient(61.04% 89.69% at 100% 100%, rgb(211 241 249) 0%, rgba(28, 210, 229, 0.08) 40.63%, rgba(28, 210, 229, 0) 100%), radial-gradient(43.78% 64.34% at 60.31% 100%, rgb(23 228 118 / 8%) 0%, rgb(23 228 118 / 0%) 100%), linear-gradient(180deg, rgb(23 228 118 / 0%) 29.44%, rgb(23 228 118 / 6%) 100%), linear-gradient(90deg, #eaebf6 0%, #ecebf9 100%)',
        boxShadow: '0px 0.3px 0.9px rgba(0, 0, 0, 0.1), 0px 1.6px 3.6px rgba(0, 0, 0, 0.13)',
        borderRadius: '4px',
      },
    },
    '& .feedbackContainer': {
      position: 'absolute',
      top: 0,
      display: 'flex',
      flexDirection: 'row',
      marginLeft: 58,
      width: 70,
      height: 32,
      backgroundColor: '#F9F9F9',
      boxShadow: '0px 0.3px 0.9px rgba(0, 0, 0, 0.1), 0px 1.6px 3.6px rgba(0, 0, 0, 0.13)',
      borderRadius: 6,
      padding: '4px 1px 4px 1px',
      border: '1px solid #A2B7F4',
      '& .likeButton': {
        height: '24px',
        visibility: 'hidden',
        '& svg': {
          height: '24px',
          width: '24px',
          visibility: 'visible',
          cursor: 'pointer',
        },
        '& svg:hover': {
          scale: '1.1',
        },
      },
      '& .dislikeButton': {
        height: '24px',
        visibility: 'hidden',
        '& svg': {
          height: '24px',
          width: '24px',
          visibility: 'visible',
          cursor: 'pointer',
        },
        '& svg:hover': {
          scale: '1.1',
        },
      },
    },
    '& .feedbackShowContainer': {
      position: 'absolute',
      top: 250,
      display: 'flex',
      flexDirection: 'row',
      marginLeft: 80,
      width: 38,
      height: 28,
      backgroundColor: '#F9F9F9',
      boxShadow: '0px 0.3px 0.9px rgba(0, 0, 0, 0.1), 0px 1.6px 3.6px rgba(0, 0, 0, 0.13)',
      borderRadius: 20,
      padding: '4px 4px 4px 4px',
      border: '1px solid #A2B7F4',
      '& .likeButton': {
        height: '20px',
        visibility: 'hidden',
        '& svg': {
          height: '20px',
          width: '20px',
          visibility: 'visible',
          cursor: 'pointer',
        },
      },
      '& .dislikeButton': {
        height: '20px',
        visibility: 'hidden',
        '& svg': {
          height: '20px',
          width: '20px',
          visibility: 'visible',
          cursor: 'pointer',
        },
      },
    },
    '& .hovered': {
      marginTop: 20,
      transition: '.2s ease',
    },
  },
  imageContainer: {
    width: 120,
    height: 140,
    backgroundColor: 'white',
    '& .image': {
      top: '50%',
      position: 'relative',
      transform: 'translateY(-50%)',
      maxWidth: 120,
      maxHeight: 140,
    },
  },
  textContainer: {
    margin: '4px 8px 0px 0px',
    padding: '0px 0px 2px 2px',
  },
  footerConatiner: {
    color: 'black',
    display: 'flex',
    flexDirection: 'column',
    marginTop: 6,
    padding: '0px 0px 2px 2px',
  },
  footerRate: {
    display: 'flex',
    flexDirection: 'row',
    alighItems: 'start',
    '& .half-star': {
      marginLeft: 4,
      alignItems: 'center',
      fontWeight: '800',
      color: '#ffb900',
    },
    '& .views': {
      marginLeft: 8,
    },
  },
  footerPrice: {
    fontSize: '12px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    height: 16,
    '& .delivery': {
      marginLeft: 8,
    },
  },
  longHeadline: {
    fontWeight: '400',
    fontSize: '18px',
    lineHeight: '24px',
    height: '20px',
    overflow: 'hidden',
    color: 'green',
  },
  finalUrl: {
    overflow: 'hidden',
    color: '#0b6a0b',
    fontSize: '14px',
    height: '20px',
    width: '80%',
  },
  description: {
    color: '#0078d4',
    marginTop: 4,
    display: 'inline-block',
    fontSize: '12px',
    flexGrow: 1,
    overflow: 'hidden',
    height: '42px',
    textOverflow: 'ellipsis',
  },
};

const useStyles = createUseStyles({
  '@global': {
    'html, body': {
      height: '100%',
      margin: 0,
      padding: 0,
      overflow: 'hidden',
    },
  },
  ...styleOverrides,
});

export const ProdAdPlacementCardPreview: React.FunctionComponent<AdPlacementCardPreviewProps> = ({
  shortHeadline,
  longHeadline,
  description,
  finalUrl,
  imageSrc,
  lowPrice,
  price,
  priceCurrencySymbol,
  shippingDescription,
}) => {
  const classes = useStyles();
  const logger = useLogger();

  const showShortHeadline = !!shortHeadline;
  const showLongHeadline = !!longHeadline;
  const showDescription = !!description;
  // const showUrl = !!displayUrl && !!finalUrl;
  // const showAdsLabelInlineWithDescription = true;
  const showImage = !!imageSrc;
  // const showLike = !!displayUrl && !!finalUrl

  const [hovered, setHovered] = useState(false);
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);

  const onLike = useCallback(() => {
    if (liked) {
      logger.logInfo('Like button cancelled');
    } else {
      logger.logInfo('Like button clicked');
      if (disliked) logger.logInfo('Dislike button cancelled');
    }
    setLiked(!liked);
    setDisliked(false);
  }, [liked, disliked, logger]);

  const onDislike = useCallback(() => {
    if (disliked) {
      logger.logInfo('Dislike button cancelled');
    } else {
      logger.logInfo('Dislike button clicked');
      if (liked) logger.logInfo('Like button cancelled');
    }
    setDisliked(!disliked);
    setLiked(false);
  }, [liked, disliked, logger]);

  const originalPrice = useMemo(() => {
    if (priceCurrencySymbol && price) {
      if (price > 1000000) {
        return `Price: ${priceCurrencySymbol}${(price / 1000000).toFixed(1)}m`;
      } if (price > 1000) {
        return `Price: ${priceCurrencySymbol}${(price / 1000).toFixed(1)}k`;
      }
      return `Price: ${priceCurrencySymbol}${price}`;
    }
    return null;
  }, [priceCurrencySymbol, price]);

  const lastFooterContent = useMemo(() => {
    if (priceCurrencySymbol && lowPrice) {
      if (lowPrice > 1000000) {
        return `on Sale: ${priceCurrencySymbol}${(lowPrice / 1000000).toFixed(1)}m`;
      } if (lowPrice > 1000) {
        return `on Sale: ${priceCurrencySymbol}${(lowPrice / 1000).toFixed(1)}k`;
      }
      return `on Sale: ${priceCurrencySymbol}${lowPrice}`;
    } if (shippingDescription) {
      return shippingDescription;
    }
    return null;
  }, [shippingDescription, priceCurrencySymbol, lowPrice]);

  return (
    <div className={classes.adPlacementContainer}>
      <div
        className="adPlacementCardPreview"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <Link
          className={hovered ? 'clickableCard hovered' : 'clickableCard'}
          target="_blank"
          rel="noopener noreferrer"
          href={finalUrl}
        >
          {
            showImage
            && (
              <div className={classes.imageContainer}>
                <img
                  src={imageSrc}
                  alt="Ad"
                  className="image"
                />
              </div>
            )
          }
          <div className={classnames('text-container-apl', classes.textContainer)}>
            {
              showShortHeadline
              && (
                <ShortHeadline
                  styleOverrides={styleOverrides}
                  shortHeadline={shortHeadline}
                />
              )
            }
            {
              showLongHeadline
              && (
                <LongHeadline
                  longHeadline={longHeadline}
                  styleOverrides={styleOverrides}
                />
              )
            }
            {
              showDescription
              && (
                <Description
                  showAdsLabel={false}
                  adsLabelText="Ad"
                  styleOverrides={styleOverrides}
                  description={description}
                />
              )
            }
          </div>
          <div className={classes.footerConatiner}>
            {
              originalPrice && (
              <div className={classes.footerPrice}>
                <div>
                  {originalPrice}
                </div>
              </div>
              )
            }
            {
              lastFooterContent && (
              <div className={classes.footerPrice}>
                {lastFooterContent}
              </div>
              )
            }
            {/* {
              showViews && <div className={classes.footerRate}>
                <div>{`${rate} / 5`} </div>
                <div className='half-star'>★</div>
              </div>
            } */}
            {/* {
              showLike && <div>
                <IconButton style={likeButtonStyle} iconProps={{ iconName: 'Like' }} title="Like" ariaLabel="Like" checked={liked} onClick={onLike} />
                <IconButton style={disLikeButtonStyle} iconProps={{ iconName: 'Dislike' }} title="Dislike" ariaLabel="Dislike" checked={disliked} onClick={onDislike} />
              </div>
            } */}
          </div>
        </Link>
        {
          hovered
            && (
            <div className="feedbackContainer">
              <ActionButton
                className="likeButton"
                onClick={onLike}
              >
                {LikeThumb()}
              </ActionButton>
              <ActionButton
                className="dislikeButton"
                onClick={onDislike}
              >
                {DislikeThumb()}
              </ActionButton>
            </div>
            )
        }
        {
          (liked || disliked)
            && (
            <div className={hovered ? 'feedbackShowContainer hovered' : 'feedbackShowContainer'}>
              {liked
                ? (
                  <ActionButton
                    className="likeButton"
                    onClick={onLike}
                  >
                    {LikeThumb()}
                  </ActionButton>
                )
                : (
                  <ActionButton
                    className="dislikeButton"
                    onClick={onDislike}
                  >
                    {DislikeThumb()}
                  </ActionButton>
                )}
            </div>
            )
        }
      </div>
    </div>
  );
};
